// Дата без времени по местному календарю (yyyy-MM-dd).
// toISOString() переводит в UTC: при UTC+5 полночь 06.10 становилась 05.10,
// и документы сохранялись на день раньше.
export const toDateOnly = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Сегодняшняя дата по местному календарю
export const todayDateOnly = (): string => toDateOnly(new Date());

// Форматирование валюты
export const formatCurrency = (amount: number, currency = 'RUB'): string => {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Форматирование числа
export const formatNumber = (value: number, decimals = 2): string => {
  return new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

// Форматирование времени в минутах
export const formatTime = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes} мин`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (remainingMinutes === 0) {
    return `${hours} ч`;
  }
  
  return `${hours} ч ${remainingMinutes} мин`;
};

// Форматирование даты
export const formatDate = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(dateObj);
};

// Форматирование даты и времени
export const formatDateTime = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(dateObj);
};

// Форматирование процентов
export const formatPercent = (value: number, decimals = 1): string => {
  return new Intl.NumberFormat('ru-RU', {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value / 100);
};

// Форматирование веса
export const formatWeight = (grams: number): string => {
  if (grams < 1000) {
    return `${grams} г`;
  }
  
  const kg = grams / 1000;
  return `${formatNumber(kg, kg % 1 === 0 ? 0 : 2)} кг`;
};

// Форматирование объема
export const formatVolume = (ml: number): string => {
  if (ml < 1000) {
    return `${ml} мл`;
  }
  
  const liters = ml / 1000;
  return `${formatNumber(liters, liters % 1 === 0 ? 0 : 2)} л`;
};

// Сокращение длинного текста
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) {
    return text;
  }
  
  return text.substring(0, maxLength - 3) + '...';
};

// Форматирование размера файла
export const formatFileSize = (bytes: number): string => {
  const sizes = ['Б', 'КБ', 'МБ', 'ГБ'];
  
  if (bytes === 0) return '0 Б';
  
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  
  return `${formatNumber(size, 1)} ${sizes[i]}`;
};

/**
 * Русское склонение по числу: pluralize(3, ['позиция', 'позиции', 'позиций']) → 'позиции'.
 * Возвращает только слово, без числа.
 */
export const pluralize = (count: number, [one, few, many]: [string, string, string]): string => {
  const n = Math.abs(count) % 100;
  const last = n % 10;
  if (n > 10 && n < 20) return many;
  if (last === 1) return one;
  if (last >= 2 && last <= 4) return few;
  return many;
};

/** Количество без лишних нулей: до 3 знаков после запятой (0,005 → «0,005», 2,5 → «2,5», 2 → «2») */
export const formatQuantity = (value: number, maxDecimals = 3): string =>
  new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: maxDecimals }).format(value);

/**
 * Порции с числом: «1 порция», «2 порции», «5 порций», а дробные — «0,5 порции», «1,25 порции»
 * (после дроби по-русски родительный падеж единственного числа)
 */
export const formatPortions = (value: number): string =>
  `${formatQuantity(value)} ${
    Number.isInteger(value) ? pluralize(value, ['порция', 'порции', 'порций']) : 'порции'
  }`;
